import { Box, Card } from '@weareberyl/design-system'
import { Collapse, Switch, message } from 'antd'
import { WebMercatorViewport } from 'deck.gl'
import { unionWith } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { sentenceCase } from 'sentence-case'
import VehiclesMap, { BasisMap, ViewState } from '~/components/Map'
import {
  ServiceAreaGeoJsonLayer,
  VehicleIconLayer,
  WorkshopIconLayers,
  WorkshopPolygonLayer,
  ZoneIconLayers,
  ZonePolygonLayer,
} from '~/components/Map/layers'
import Legend, { LegendProps } from '~/components/Map/Legend'
import withSchemeServiceAreas, {
  SERVICE_AREAS_GEOJSON,
} from '~/components/Scheme/withSchemeServiceAreas'
import { currentLocation } from '~/utils'

import { REDUCED_POLL_INTERVAL } from '~/client'
import { SCHEME } from '~/types'
import { calculateSchemeCoordinates } from '~/utils/calculateSchemeCoordinates'

import HeadTitle from '~/components/HeadTitle'
import {
  LocationAreaInput,
  JourneySetMapQuery_vehicles_VehicleConnection_nodes_Vehicle as Vehicle,
  VehicleCategory,
  VehicleLockState,
  VehicleState,
  VehiclesMapQueryVariables,
  VehiclesMapQuery_all_zones_ZoneConnection_nodes_Zone as Zone,
  ZoneType,
  useVehiclesMapQuery,
} from '~/gql/generated/graphql'
import useContainerSize from '~/hooks/useContainerSize'
import Loading from '../Loading'

const { Panel } = Collapse

const VehicleCategoryNames = {
  bike: 'Bike',
  cargo_ebike: 'e-Cargo Bike',
  ebike: 'e-Bike',
  escooter: 'e-Scooter',
}

type MapViewProps = {
  scheme: SCHEME
  serviceAreas: SERVICE_AREAS_GEOJSON
}

export const VehiclesMapContainer = ({
  scheme,
  serviceAreas,
}: MapViewProps) => {
  const [legendProps, setLegendProps] = useState<LegendProps>({ type: null })
  const [coords, setCoords] = useState<{
    longitude: number | undefined
    latitude: number | undefined
  }>({
    longitude: undefined,
    latitude: undefined,
  })
  const [bounds, setBounds] = useState<LocationAreaInput | null>({
    latitude_min: 0,
    latitude_max: 0,
    longitude_min: 0,
    longitude_max: 0,
  })

  const [pendingLocation, setPendingLocation] = useState(false)

  const [showVehicles, setShowVehicles] = useState(true)
  const [showZones, setShowZones] = useState(true)
  const [showWorkshops, setShowWorkshops] = useState(true)

  const [showLiveZones, setShowLiveZones] = useState(true)
  const [showSuspendedZones, setShowSuspendedZones] = useState(false)
  const [showLockedModules, setShowLockedModules] = useState(true)
  const [showUnlockedModules, setShowUnlockedModules] = useState(true)
  const [vehicleCategory, setVehicleCategory] = useState<VehicleCategory[]>([])
  const [vehicleState, setVehicleState] = useState<VehicleState[]>([])

  const { containerRef, size } = useContainerSize()
  const viewport = useMemo(
    () =>
      new WebMercatorViewport({
        ...BasisMap.defaultProps,
        ...size,
      }),
    [size],
  )

  const fetchLocation = async () => {
    setPendingLocation(true)
    const coords = await currentLocation()
    setCoords(coords)
    setPendingLocation(false)
  }

  useEffect(() => {
    fetchLocation()
  }, [])

  const vehicleFilters = useMemo(() => {
    const lockStates: VehicleLockState[] = []
    const filters: VehiclesMapQueryVariables = {}

    if (showLockedModules) {
      lockStates.push(VehicleLockState.locked)
    }
    if (showUnlockedModules) {
      lockStates.push(VehicleLockState.unlocked)
    }

    if (vehicleCategory.length) {
      filters.vehicle_category = vehicleCategory
    }

    if (vehicleState.length) {
      filters.vehicle_state = vehicleState
    }

    if (lockStates.length) {
      filters.vehicle_lock_state = lockStates
    }

    return filters
  }, [showLockedModules, showUnlockedModules, vehicleCategory, vehicleState])

  const zoneFilters = useMemo(() => {
    let suspendedStates
    if (showLiveZones && !showSuspendedZones) {
      suspendedStates = false
    }
    if (!showLiveZones && showSuspendedZones) {
      suspendedStates = true
    }
    return suspendedStates
  }, [showLiveZones, showSuspendedZones])

  const { data, loading, error, previousData, fetchMore } = useVehiclesMapQuery(
    {
      variables: {
        scheme_id: scheme.id,
        location: bounds,
        in_zone: false,
        suspended: zoneFilters,
        ...vehicleFilters,
      },
      pollInterval: 0,
    },
  )

  useEffect(() => {
    if (error) {
      message.error(error.message)
    }
  }, [error])

  const toggleVehicleLayer = () => {
    setShowVehicles(showVehicles => !showVehicles)
  }

  const toggleZoneLayer = () => {
    setShowZones(showZones => !showZones)
  }

  const toggleWorkshopLayer = () => {
    setShowWorkshops(showWorkshops => !showWorkshops)
  }

  const clearLegend = () => {
    setLegendProps({ type: null })
  }

  const generateServiceAreasLayer = () =>
    ServiceAreaGeoJsonLayer({
      serviceAreas,
      layerId: 'service-area-layer',
      layerProps: {
        onClick: ({ object: { properties } }) => {
          setLegendProps({
            type: 'ServiceArea',
            service_area: properties,
          })
        },
      },
    })

  const generateNormalZonePolygonLayer = (zones: Zone[]) => {
    const visibleZoneId =
      legendProps?.type === 'Zone' ? legendProps.zone_id : null
    return ZonePolygonLayer({
      zones,
      layerId: 'zone-polygon-layer',
      visible: showZones,
      visibleZoneId,
    })
  }

  const generateWorkshopZonePolygonLayer = (zones: Zone[]) => {
    return WorkshopPolygonLayer({
      zones,
      layerId: 'workshop-polygon-layer',
      visible: showWorkshops,
      onClick: obj => {
        setLegendProps({
          type: 'Zone',
          zone_id: obj?.object.properties.id,
        })
      },
    })
  }

  const generateVehicleIconLayer = (vehicles: Vehicle[]) =>
    VehicleIconLayer({
      vehicles,
      layerId: 'vehicles-icon-layer',
      layerProps: {
        visible: showVehicles,
        onClick: ({ object }) => {
          setLegendProps({
            type: 'Vehicle',
            module_id: object?.detail?.module?.id,
          })
        },
      },
    })

  const generateParkingZoneLayers = (zones: Zone[]) =>
    ZoneIconLayers({
      zones,
      layerId: 'zone-layer',
      iconLayerProps: {
        visible: showZones,
        onClick: ({ object }) => {
          setLegendProps({ type: 'Zone', zone_id: object.id })
        },
      },
    })

  const generateWorkshopParkingZoneLayers = (zones: Zone[]) =>
    WorkshopIconLayers({
      zones,
      layerId: 'workshop-layer',
      iconLayerProps: {
        visible: showWorkshops,
        onClick: ({ object }) => {
          setLegendProps({ type: 'Zone', zone_id: object.zone.id })
        },
      },
    })

  const fetchData = (viewState: ViewState) => {
    setBounds({
      longitude_min: viewState.bounds._ne.lng,
      longitude_max: viewState.bounds._sw.lng,
      latitude_min: viewState.bounds._ne.lat,
      latitude_max: viewState.bounds._sw.lat,
    })
    fetchMore({
      variables: {
        location: bounds,
        scheme_id: scheme.id,
        in_zone: false,
        where: vehicleFilters,
        suspended: zoneFilters,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev
        }

        if (!prev) {
          return fetchMoreResult
        }

        // Merge the data from the previous query, if any duplicates occur then take the new value
        const vehicleNodes = unionWith(
          fetchMoreResult?.vehicles?.nodes,
          prev?.vehicles?.nodes,
          (p, n) => p.id === n.id,
        )

        // Merge the data from the previous query, if any duplicates occur then take the new value
        const zoneNodes = unionWith(
          fetchMoreResult?.all_zones?.nodes,
          prev?.all_zones?.nodes,
          (p, n) => p.id === n.id,
        )

        return {
          ...prev,
          vehicles: {
            __typename: prev?.vehicles?.__typename,
            nodes: vehicleNodes,
          },
          zones: {
            __typename: prev?.all_zones?.__typename,
            nodes: zoneNodes,
          },
        }
      },
    })
  }

  const usableData = data ?? previousData

  const allZones = usableData?.all_zones?.nodes ?? []
  const normalZones = allZones.filter(z => z.zone_type === ZoneType.normal)
  const workshopZones = allZones.filter(z => z.zone_type === ZoneType.workshop)

  const vehicles = usableData?.vehicles?.nodes ?? []

  const schemeCoords = calculateSchemeCoordinates(serviceAreas, viewport)
  const latitude = schemeCoords?.coords?.latitude ?? coords?.latitude
  const longitude = schemeCoords?.coords?.longitude ?? coords?.longitude

  return (
    <div
      className="main-map"
      ref={containerRef}
      style={{ height: '100%', width: '100%' }}
    >
      <HeadTitle pageTitle="Overview" />
      {pendingLocation || (loading && !previousData) ? (
        <Loading />
      ) : (
        <VehiclesMap
          layers={[
            generateServiceAreasLayer(),
            generateNormalZonePolygonLayer(normalZones),
            generateWorkshopZonePolygonLayer(workshopZones),
            generateVehicleIconLayer(vehicles),
            ...generateParkingZoneLayers(normalZones),
            ...generateWorkshopParkingZoneLayers(workshopZones),
          ]}
          latitude={latitude}
          longitude={longitude}
          zoom={schemeCoords.zoom !== Infinity ? schemeCoords.zoom : 15}
          pollInterval={REDUCED_POLL_INTERVAL}
          onViewStateChange={fetchData}
        >
          {legendProps.type && (
            <Legend {...legendProps} onClose={clearLegend} />
          )}
          <Card
            className="map-filter-card"
            style={{
              position: 'absolute',
              padding: 0,
              top: 20,
              right: 20,
              width: 180,
            }}
            id="map-filter-card"
          >
            <Box p={4} pb={2}>
              <div
                style={{
                  color: '#52525c',
                  fontSize: '16px',
                  fontWeight: 600,
                  paddingBottom: '16px',
                }}
              >
                Layers
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <p>Vehicles</p>
                <Switch checked={showVehicles} onChange={toggleVehicleLayer} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <p>Bays</p>
                <Switch checked={showZones} onChange={toggleZoneLayer} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <p style={{ marginBottom: 0 }}>Workshops</p>
                <Switch
                  checked={showWorkshops}
                  onChange={toggleWorkshopLayer}
                />
              </div>
            </Box>
            <Box px={4} pt={2}>
              <div
                style={{
                  color: '#52525c',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
              >
                Filters
              </div>
            </Box>
            <Collapse style={{ border: 0, paddingTop: 0 }} accordion>
              <Panel
                header="Lock State"
                key="1"
                style={{
                  backgroundColor: 'white',
                  border: 0,
                }}
              >
                <Box px={2}>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <p>Locked</p>
                    <Switch
                      checked={showLockedModules || !showUnlockedModules}
                      onChange={() => setShowLockedModules(l => !l)}
                    />
                  </div>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <p>Unlocked</p>
                    <Switch
                      checked={showUnlockedModules || !showLockedModules}
                      onChange={() => setShowUnlockedModules(u => !u)}
                    />
                  </div>
                </Box>
              </Panel>
              <Panel
                header="Vehicle State"
                key="2"
                style={{
                  backgroundColor: 'white',
                  border: 0,
                }}
              >
                <Box px={2}>
                  {Object.keys(VehicleState).map(state => (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                      key={`option-${state}`}
                    >
                      <p>{sentenceCase(state)}</p>
                      <Switch
                        checked={
                          vehicleState.includes(state as VehicleState) ||
                          vehicleState.length === 0
                        }
                        onChange={() => {
                          if (vehicleState.includes(state as VehicleState)) {
                            setVehicleState(v => v.filter(s => s !== state))
                          } else {
                            setVehicleState(v => [state as VehicleState, ...v])
                          }
                        }}
                      />
                    </div>
                  ))}
                </Box>
              </Panel>
              <Panel
                header="Vehicle Category"
                key="3"
                style={{
                  backgroundColor: 'white',
                  border: 0,
                }}
              >
                <Box px={2}>
                  {Object.keys(VehicleCategory).map(category => (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                      key={`option-${category}`}
                    >
                      <p>{VehicleCategoryNames[category]}</p>
                      <Switch
                        checked={
                          vehicleCategory.includes(
                            category as VehicleCategory,
                          ) || vehicleCategory.length === 0
                        }
                        onChange={() => {
                          if (
                            vehicleCategory.includes(
                              category as VehicleCategory,
                            )
                          ) {
                            setVehicleCategory(v =>
                              v.filter(c => c !== category),
                            )
                          } else {
                            setVehicleCategory(v => [
                              category as VehicleCategory,
                              ...v,
                            ])
                          }
                        }}
                      />
                    </div>
                  ))}
                </Box>
              </Panel>
              <Panel
                header="Bay State"
                key="4"
                style={{
                  backgroundColor: 'white',
                  border: 0,
                }}
              >
                <Box px={2}>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <p>Live</p>
                    <Switch
                      checked={showLiveZones}
                      onChange={() => setShowLiveZones(lz => !lz)}
                    />
                  </div>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <p>Suspended</p>
                    <Switch
                      checked={showSuspendedZones}
                      onChange={() => setShowSuspendedZones(sz => !sz)}
                    />
                  </div>
                </Box>
              </Panel>
            </Collapse>
          </Card>
        </VehiclesMap>
      )}
    </div>
  )
}

export default withSchemeServiceAreas(VehiclesMapContainer)
